import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FinalClientService } from '../../../services/final-client.service';
import { ActivatedRoute, Router } from '@angular/router';
import { first, map } from 'rxjs/internal/operators';
import { CountyDTO } from "../../../../shared/models/CountyDTO";
import { CityDTO } from "../../../../shared/models/CityDTO";
import { AddressService } from "../../../../shared/services/address.service";
import { PlaceDetailsGas } from '../../../models/PlaceDetailsGas';

@Component({
  selector: 'app-gas-consumption-place-details',
  templateUrl: './gas-consumption-place-details.component.html',
  styleUrls: ['./gas-consumption-place-details.component.scss']
})
export class GasConsumptionPlaceDetailsComponent implements OnInit, OnDestroy {

  public form: FormGroup;
  public conventionParts: string[] = [];
  public suppliers: string[] = [];
  private routeDataSub: Subscription;
  public placeId: number;
  public isFormSubmitAttempt = false;
  public placeDetails: PlaceDetailsGas;
  public counties: CountyDTO[] = [];
  public cities: CityDTO[] = [];

  @Input()
  public editable: boolean = false;

  @Input()
  set place(place: any) {
    this.form.patchValue(place);
    if (place.conventionParties) {
      this.conventionParts = place.conventionParties;
    }
    if (place.suppliers) {
      this.suppliers = place.suppliers;
    }
    this.addressService.getCities(this.form.get('county').value).subscribe((cities: CityDTO[]) => {
      this.cities = cities;
    });
  }

  @Input()
  public isContractChange: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private finalClientService: FinalClientService,
    private route: ActivatedRoute,
    private router: Router,
    private addressService: AddressService
  ) {
    this.addressService.getCounties().subscribe(counties => {
      this.counties = counties;
    });
    this.createConsumptionPlaceForm();
  }

  createConsumptionPlaceForm(): void {
    this.form = this.formBuilder.group({
      operatorName: ['', Validators.required],
      code: ['', Validators.required],
      placeType: [''],
      isProsumer: ['', Validators.required],
      conventionParties: [''],
      suppliers: [''],
      //  address
      streetNumber: ['', Validators.required],
      streetName: ['', Validators.required],
      postalCode: ['', Validators.required],
      block: [''],
      apartment: [''],
      floor: [''],
      county: ['', Validators.required],
      city: ['', Validators.required],
      //convention
      conventionType: [''],
      conventionNumber: [''],
      conventionDate: [''],
      conventionPresent: [''],
      //technical data
      debit: ['', Validators.required],
      pressure: ['', Validators.required],
      maximumPressure: ['', Validators.required],
      minimumPressure: ['', Validators.required],
      minimumTechnicalPressure: ['', Validators.required],
      reservedCapacity: ['', Validators.required],
      smartMeter: ['', Validators.required],
      counterType: ['', Validators.required],
      counterSeries: ['', Validators.required],
      technicalDataStatus: ['', Validators.required],
      networkType: ['', Validators.required],
      counterIndexInterval: ['', Validators.required],
      operatingMode: ['', Validators.required],
      consumptionCategory: ['', Validators.required],
      counterIndexDesc: ['', Validators.required],
      counterIndexValue: [],
      counterIndexReadDate: [],

      //network contract
      clientIdentificationNumber: [''],
      clientName: [''],
      contractNumber: [''],
      validFrom: [''],
      validTo: [''],
      contractType: [''],

      //connection certificate
      accessAgreement: [''],
      accessAgreementNumber: [''],
      accessAgreementDate: [''],

      //connection notice
      connectionNotice: [''],
      connectionNoticeNumber: [''],
      connectionNoticeDate: [''],
    });
  }

  getFormData(): void {
    if (this.placeId) {
      this.finalClientService.getFCGasConsumptionPlace(this.placeId).pipe(first())
        .subscribe((placeDetailsGas: PlaceDetailsGas) => {
          this.form.patchValue(placeDetailsGas);
          this.addressService.getCities(this.form.get('county').value).subscribe((cities: CityDTO[]) => {
            this.cities = cities;
          }
          );
        });
    }
  }

  ngOnInit(): void {
    this.routeDataSub = this.route
      .paramMap
      .pipe(
        map(value => {
          if (window.history.state.editable) {
            this.editable = !!window.history.state.editable;
          }
          this.placeId = +window.history.state.placeId;
          return this.placeId;
        })
      ).subscribe((validationDetails) => this.getFormData());
  }

  onSubmit(): void {
    this.isFormSubmitAttempt = true;
  }

  onChangeCounty(): void {
    this.cities = [];
    this.addressService.getCities(this.form.get('county').value).subscribe((cities: CityDTO[]) => this.cities = cities);
  }

  isConventionPresent(): boolean {
    return this.form.get('conventionPresent').value === null || this.form.get('conventionPresent').value === false;
  }

  isDirectNetworkContractType(): boolean {
    if (this.suppliers == null) {
      return true;
    } else if (this.suppliers.length > 0) {
      return false;
    } else {
      return true;
    }
  }

  isConsumptionPlaceConnected(): boolean {
    return this.form.get('technicalDataStatus').value === 'CONECTAT';
  }

  isConsumptionPlaceDisconnected(): boolean {
    return this.form.get('technicalDataStatus').value === 'DECONECTAT';
  }

  isConsumptionPlaceDezafectat(): boolean {
    return this.form.get('technicalDataStatus').value === 'DEZAFECTAT';
  }

  isConsumptionPlaceInactive(): boolean {
    return this.form.get('technicalDataStatus').value === 'INACTIV';
  }

  isNetworkTypeDistribution(): boolean {
    return this.form.get('networkType').value === 'D';
  }

  isNetworkTypeTransport(): boolean {
    return this.form.get('networkType').value === 'T';
  }

  isNetworkTypeCA(): boolean {
    return this.form.get('networkType').value === 'CA';
  }

  isOperatingModeSeasonal(): boolean {
    return this.form.get('operatingMode').value === 'SEZONIER';
  }

  isOperatingModePermanent(): boolean {
    return this.form.get('operatingMode').value === 'PERMANENT';
  }

  isSmartMeter(): boolean {
    return this.form.get('smartMeter').value === null || this.form.get('smartMeter').value === false;
  }

  isProsumer(): boolean {
    return this.form.get('isProsumer').value === null || this.form.get('isProsumer').value === false;
  }

  setTechnicalDataStatus(value): void {
    this.form.controls['technicalDataStatus'].setValue(value);
  }

  setProsumer(value): void {
    this.form.controls['isProsumer'].setValue(value);
  }

  setOperatingMode(value): void {
    this.form.controls['operatingMode'].setValue(value);
  }

  setNetworkType(value): void {
    this.form.controls['networkType'].setValue(value);
  }

  setProperty(): string {
    if (this.editable) {
      return "enabled";
    } else {
      return "disabled";
    }
  }

  ngOnDestroy(): void {
    this.routeDataSub.unsubscribe();
  }

}
